import { useMemo, useState } from 'react';
import { Truck } from 'lucide-react';
import { toast } from 'react-toastify';

import { Modal } from '../../../../components/Modal/Modal';
import { SearchableSelect } from '../../../../components/SearchableSelect/SearchableSelect';
import { useListCarriers } from '../../../carriers/api';
import { coiWarningText } from '../../../carriers/coi';
import { canAssignCarrierFromStatus } from '../../manageLoadForm/constants';
import { useBulkAssignCarrier } from '../../api';
import { orderIdOf } from '../grain';
import { summarizeBulk } from '../summarize';
import { planAssignCarrier, advanceableLoads } from './assignCarrierPlan';
import type { BulkAction, BulkActionBarContext } from '../types';

const toTarget = (row: any) => ({
  orderId: orderIdOf(row) ?? '',
  itemId: row.id,
  loadNumber: row.loadNumber,
});

// Assign one carrier to every eligible selected load. PENDING loads (no warehouse
// yet) are skipped; an opt-in advances the dispatchable loads to Assigned.
function AssignCarrierDialog({ ctx, onClose }: { ctx: BulkActionBarContext; onClose: () => void }) {
  const { rows, count } = ctx;
  const { data } = useListCarriers();
  const bulkAssign = useBulkAssignCarrier();
  const isApplying = bulkAssign.isPending;

  const [carrierId, setCarrierId] = useState('');
  const [advance, setAdvance] = useState(true);

  const carriers = useMemo(() => data?.carriers ?? [], [data]);
  const carrierOptions = useMemo(
    () => carriers.map((c) => ({ id: c.id, name: c.companyName })),
    [carriers]
  );
  const selectedCarrier = carriers.find((c) => c.id === carrierId) ?? null;
  const coiWarning = selectedCarrier
    ? coiWarningText(selectedCarrier.coiOnFile, selectedCarrier.coiExpiresAt)
    : null;

  const plan = useMemo(() => planAssignCarrier(rows), [rows]);
  const advanceable = useMemo(() => advanceableLoads(plan.eligible), [plan.eligible]);
  const willAdvance = advance ? advanceable.length : 0;

  const apply = async () => {
    if (!carrierId || plan.eligible.length === 0) return;

    try {
      const res = await bulkAssign.mutateAsync({
        items: plan.eligible.map(toTarget),
        carrierId,
        advanceItemIds: advance ? advanceable.map((row) => row.id) : [],
      });

      const summary = summarizeBulk(res.succeeded, res.failed, plan.eligible.length, 'assigned');
      if (summary.tone === 'success') toast.success(summary.message);
      else toast.warn(summary.message);
      if (summary.clearSelection) ctx.onClearSelection();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to assign the carrier.');
    }
  };

  return (
    <Modal
      isOpen
      onClose={isApplying ? () => {} : onClose}
      title={`Assign a carrier to ${plan.eligible.length} load${
        plan.eligible.length === 1 ? '' : 's'
      }`}
      size="sm"
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={isApplying}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            onClick={apply}
            disabled={isApplying || !carrierId || plan.eligible.length === 0}
          >
            {isApplying ? 'Assigning…' : `Assign to ${plan.eligible.length}`}
          </button>
        </>
      }
    >
      {plan.eligible.length === 0 ? (
        <p>
          None of the {count} selected load{count === 1 ? '' : 's'} can take a carrier — a pending
          load has no warehouse yet. Approve it first.
        </p>
      ) : (
        <p>
          <strong>{plan.eligible.length}</strong> of {count} selected load
          {count === 1 ? '' : 's'} will be assigned to the chosen carrier.
        </p>
      )}

      {plan.skippedPending.length > 0 && (
        <ul className="bulk-confirm__list">
          <li>
            {plan.skippedPending.length} skipped (awaiting approval — open the load to approve it).
          </li>
        </ul>
      )}

      <label className="bulk-bar__label" htmlFor="bulk-carrier" style={{ marginTop: 12 }}>
        Carrier
      </label>
      <SearchableSelect
        value={carrierId}
        options={carrierOptions}
        onChange={setCarrierId}
        placeholder="Search carriers…"
        ariaLabel="Carrier"
      />

      {coiWarning && <div className="bulk-confirm__warning">{coiWarning}</div>}

      {advanceable.length > 0 && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
          <input
            type="checkbox"
            checked={advance}
            onChange={(e) => setAdvance(e.target.checked)}
            disabled={isApplying}
          />
          Also move the {willAdvance} dispatchable load{willAdvance === 1 ? '' : 's'} to Assigned
        </label>
      )}
    </Modal>
  );
}

function AssignCarrierTrigger({ ctx }: { ctx: BulkActionBarContext }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        onClick={() => setOpen(true)}
        disabled={ctx.isApplyingStatus}
      >
        <Truck size={14} />
        Assign carrier
      </button>

      {open && <AssignCarrierDialog ctx={ctx} onClose={() => setOpen(false)} />}
    </>
  );
}

export const AssignCarrierAction: BulkAction = {
  id: 'assign-carrier',
  isAvailable: (ctx) => ctx.rows.some((row) => canAssignCarrierFromStatus(row?.status)),
  Component: AssignCarrierTrigger,
};
